import React, { MouseEventHandler } from 'react'
import { LanguageContext } from '../../contexts'
import { getWorkshopEventHtmlId, WorkshopEvent as EventModel } from '../../Models/workshops'
import { Icons, WorkshopStatusIds, WorkshopStatusIdStatuses, WorkshopTypeIds } from '../../constants'
import { LanguageIdLanguages, LocalizedText } from '../../language'
import { format } from '../../format'
import { fillTemplateString, isBrowserMobile, isElementInViewport } from '../../utility'
import { EventCollapsedProps } from './EventCollapsed'
import { Notification } from '../../Components/Notification'
import { EmailLink, PhoneLink, MapLink } from '../../Components/Link'
import { RegistrationOperation } from '../../Models/participant'
import { RegistrationMessage } from '../../Models/system'
import classNames from 'classnames'

import './EventExpanded.scss'
import { ParticipationProgress } from './ParticipationProgress'

export interface EventExpandedProps extends EventCollapsedProps {
	onClick: MouseEventHandler<HTMLDivElement>
	onRegisterClick: (operation: RegistrationOperation) => void
	onCloseClick: () => void
	onDismissRegistrationSuccessfulMessage: (workshopEventId: number) => void
	registrationMessage?: RegistrationMessage
}

export const EventExpanded = React.forwardRef((props: EventExpandedProps, ref: React.LegacyRef<HTMLDivElement>) => {
	const { localizedText } = React.useContext(LanguageContext)

	React.useEffect(() => {
		const thisEventElement = document.getElementById(getWorkshopEventHtmlId(props.event))
		if (thisEventElement && !isElementInViewport(thisEventElement)) {
			thisEventElement?.scrollIntoView(isBrowserMobile())
		}
	}, [])

	const registrationSuccessFullMessage = props.registrationMessage ? 
		<Notification 
			colorType={props.registrationMessage.color}
			isLight={true}
			onDismiss={() => props.onDismissRegistrationSuccessfulMessage(props.event.EventId)}>
			{props.registrationMessage.text}
		</Notification> : 
		null

	const registerButton = props.event.IsParticipating ? 
		<button className='button is-medium is-danger  is-fullwidth' onClick={() => props.onRegisterClick('Deregister')}>{localizedText.Unregister}</button> : 
		<button className='button is-medium is-primary is-fullwidth' onClick={() => props.onRegisterClick('Register')}>{localizedText.Register}</button> 

	const participationContent = props.event.IsParticipating ? null : 
		<>
			{fillTemplateString(localizedText.SeatsAvailableTemplate, props.event)}
			<ParticipationProgress seatsLeft={props.event.SeatsLeft} maximumParticipants={props.event.MaximumParticipants} />
		</>

	let tentativeStatusIndicator = (props.event.StatusId == WorkshopStatusIds.Tentative) ? 
		<span className='tag is-warning is-medium ml-2' title={localizedText.ThisWorkshopIsTentativelyScheduled}>{localizedText[WorkshopStatusIdStatuses[props.event.StatusId]]}</span> : 
		null

	let workshopIsOnline = props.event.WorkshopTypeId == WorkshopTypeIds.Online

	let location = workshopIsOnline ? 
		<><span>{localizedText['Online']} {Icons.laptop}  </span>{tentativeStatusIndicator}</> : 
		<><span>{props.event.Location.Name}</span>{tentativeStatusIndicator}</>

	let mapLink = props.event.WorkshopTypeId == WorkshopTypeIds.InPerson ?  
		<MapLink location={props.event.Location} /> : null

	const showCounty = !workshopIsOnline && !!props.event.Location.MunicipalityTier1Name

	const county = !showCounty ? null : 
		<p>{localizedText.County}: {props.event.Location.MunicipalityTier1Name}</p>

	let handleCloseClick: MouseEventHandler<HTMLButtonElement> = (e) => {
		props.onCloseClick()
		e.stopPropagation()
	}

	return (
		<div ref={ref} id={getWorkshopEventHtmlId(props.event)} className='block workshop-event-expanded' onClick={props.onClick}>
			<div className='card'>
				<div className='card-header has-background-primary-light'>
					<div className='columns'>
						<div className='column is-3'>
							<button className='delete is-pulled-right collapse event button' onClick={handleCloseClick}></button>
							<p>
								{localizedText.Language}: {localizedText[LanguageIdLanguages[props.event.LanguageId]]}
							</p>
						</div>
						<div className='column is-6 has-text-centered'>
							<p className='is-size-4'>
								{localizedText.WorkshopLeader}: {props.event.Instructor.FirstName} {props.event.Instructor.LastName}
							</p>
							<p>
								<EmailLink email={props.event.Instructor.EmailAddress} />
							</p>
							<p>
								<PhoneLink phoneNumber={props.event.Instructor.Phone} />
							</p>
						</div>
						<div className='column is-3 has-text-right'>
							{registerButton}
							{participationContent}
						</div>
					</div>
				</div>

				<div className='card-content'>
					<div className='is-clearfix'>
						<div className='is-pulled-left'>
							{location}
							{county}
						</div>
						<div className='is-pulled-right'> 
							{mapLink}
						</div>
					</div>
					<hr />
					<p className='mb-4'>
						{props.event.ParticipantInstructions}
					</p>
					<EventScheduleTable event={props.event} localizedText={localizedText} />
				</div>
				{registrationSuccessFullMessage}
			</div>
		</div>
	)
})

interface EventScheduleTableProps {
	event: EventModel
	localizedText: LocalizedText
}

function EventScheduleTable(props: EventScheduleTableProps) {

	const scheduleRows = props.event.Schedule.map((schedule, scheduleIndex) => {

		// highlight if schedule is tentative
		const status = 
			<span className={classNames({ 'tag is-warning is-medium': schedule.StatusId == WorkshopStatusIds.Tentative })}>
				{props.localizedText[WorkshopStatusIdStatuses[schedule.StatusId]]}
			</span>

		return (
			<tr key={scheduleIndex}>
				<td>{format.day(schedule.StartDateAndTime)}</td>
				<td>{format.date(schedule.StartDateAndTime)}</td>
				<td>{format.time(schedule.StartDateAndTime)}</td>
				<td>{format.time(schedule.EndDateAndTime)}</td>
				<td className='hidden-on-mobile'>{schedule.Room}</td>
				<td className='hidden-on-mobile'>{status}</td>
			</tr>
		)
	})

	return (
		<table className='table is-fullwidth'>
			<thead>
				<tr>
					<th>{props.localizedText.Day}</th>
					<th>{props.localizedText.Date}</th>
					<th>{props.localizedText.Start}</th>
					<th>{props.localizedText.Finish}</th>
					<th className='hidden-on-mobile'>{props.localizedText.Room}</th>
					<th className='hidden-on-mobile'>{props.localizedText.Status}</th>
				</tr>
			</thead>
			<tbody>
				{scheduleRows}
			</tbody>
		</table>
	)
}